/**
 * Cache timestamp management utilities.
 * Handles writing GeoAPI metadata timestamps to IndexedDB for cache staleness detection.
 */
import { MAP_STORES, setCacheMetadata, generateCacheKey } from './store.js'

/**
 * @typedef {Object.<string, number>} SemesterTimestamps
 * Object mapping semester IDs to Unix timestamps (seconds since epoch)
 */

/**
 * @typedef {Object.<string, number>} HourlyTimestamps
 * Object mapping semester IDs to Unix timestamps (seconds since epoch) for hourly data
 */

/**
 * @typedef {Object} GeoApiFileMetadata
 * @property {number} [resources] - Unix timestamp of resources file modification
 * @property {number} [datasources] - Unix timestamp of datasources file modification
 * @property {SemesterTimestamps} [semesters] - Nested object of semester timestamps keyed by semester ID
 * @property {HourlyTimestamps} [hourly] - Nested object of hourly timestamps keyed by semester ID
 */

/**
 * @typedef {Object.<string, GeoApiFileMetadata>} GeoApiMetadata
 * Metadata object keyed by school ID containing file modification timestamps
 */

/**
 * Writes GeoAPI file modification timestamps to IndexedDB cache metadata.
 * Called by landing component after API response to populate backend timestamps.
 * These timestamps are used by staleness checks to determine if cached data is outdated.
 *
 * @param {GeoApiMetadata} geoApiMetadata - Metadata keyed by school ID with file timestamps
 * @returns {Promise<void>}
 *
 * @example
 * await writeCacheTimestamps({
 *   "school-123": {
 *     "resources": 1732197000,
 *     "datasources": 1732197000,
 *     "semesters": {
 *       "abc": 1732201500
 *     },
 *     "hourly": {
 *       "abc": 1732201500
 *     }
 *   }
 * })
 */
export const writeCacheTimestamps = async (geoApiMetadata) => {
  if (!geoApiMetadata || typeof geoApiMetadata !== 'object') {
    console.warn('writeCacheTimestamps: invalid geoApiMetadata', geoApiMetadata)
    return
  }

  for (const [schoolId, timestamps] of Object.entries(geoApiMetadata)) {
    if (!timestamps || typeof timestamps !== 'object') {
      console.warn(`writeCacheTimestamps: invalid timestamps for school ${schoolId}`, timestamps)
      continue
    }

    // Write resources timestamp
    if (timestamps.resources) {
      const key = generateCacheKey(MAP_STORES.RESOURCES, schoolId)
      await setCacheMetadata(key, {
        backendFileModifiedAt: timestamps.resources,
      })
    }

    // Write datasources timestamp
    if (timestamps.datasources) {
      const key = generateCacheKey(MAP_STORES.DATASOURCES, schoolId)
      await setCacheMetadata(key, {
        backendFileModifiedAt: timestamps.datasources,
      })
    }

    // Write semester timestamps from nested semesters object
    if (timestamps.semesters && typeof timestamps.semesters === 'object') {
      for (const [semesterId, timestamp] of Object.entries(timestamps.semesters)) {
        const key = generateCacheKey(MAP_STORES.SEMESTERS, schoolId, semesterId)
        await setCacheMetadata(key, {
          backendFileModifiedAt: timestamp,
        })
      }
    }

    // Write hourly timestamps from nested hourly object
    if (timestamps.hourly && typeof timestamps.hourly === 'object') {
      for (const [semesterId, timestamp] of Object.entries(timestamps.hourly)) {
        const key = generateCacheKey(MAP_STORES.HOURLY, schoolId, semesterId)
        await setCacheMetadata(key, {
          backendFileModifiedAt: timestamp,
        })
      }
    }
  }
}
